import { createApp } from 'vue';
import { createPinia } from 'pinia';
import VueApexCharts from "vue3-apexcharts";
import App from "./App.vue";
import router from "./router";
// import { useOrganizationStore } from '@/store/organization/store.js';
import store from "./store";
import * as api from "./utils/api";
import * as authhelper from "./utils/auth-helper";
import * as fhirhelper from "./utils/fhir-helpers";

// Import only the essential components globally
import failoverImage from "./components/shared/failoverImage.vue";
import Avatar from './components/shared/avatar.vue';
import DatePicker from './components/shared/datePicker.vue';
import InstanceSelector from "./components/shared/InstanceSelector.vue";
// Other components should be lazy-loaded in their respective routes
import { Amplify } from 'aws-amplify';
import { registerPlugins } from "./plugins";
import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';
import { AgChartsCommunityModule } from 'ag-charts-community';
import './utils/ag-grid-config';
import { useNotificationsStore } from '@/store/notifications/index.js';
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum';
import { initConfettiListeners } from './utils/confetti';

// FilePond styles for shared upload component
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

// init
registerServiceWorker();
init();

function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('ServiceWorker registration successful with scope: ', registration.scope);

          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  console.log('New content is available; please refresh.');
                }
              }
            });
          });
        })
        .catch(error => {
          console.error('ServiceWorker registration failed: ', error);
        });
    });
  }
}

async function init() {
  const pinia = createPinia();
  // mount app
  const app = createApp(App);

  var config = window.config;
  if (!config) {
    // Check for cached config first to speed up initialization
    const cachedConfig = localStorage.getItem('initConfig');
    const configTimestamp = localStorage.getItem('initConfigTimestamp');
    const now = Date.now();
    
    // Use cached config if it's less than 5 minutes old
    if (cachedConfig && configTimestamp && (now - parseInt(configTimestamp, 10)) < 300000) {
      try {
        config = JSON.parse(cachedConfig);
        console.log('Using cached initialization config');
      } catch (e) {
        console.warn('Failed to parse cached config, fetching fresh');
        localStorage.removeItem('initConfig');
        localStorage.removeItem('initConfigTimestamp');
      }
    }
    
    // Fetch fresh config if no valid cache
    if (!config) {
      console.log('Fetching fresh initialization config');
      var response = await api.GetInit();
      config = response?.data;
      
      // Cache the config for future use
      if (config) {
        localStorage.setItem('initConfig', JSON.stringify(config));
        localStorage.setItem('initConfigTimestamp', now.toString());
      }
    }
  }

  // Configure Amplify first
  Amplify.configure(authhelper.getAWSExport(config.awsConfig));

  // Then register other plugins
  registerPlugins(app); // PDF worker and other core plugins
  app.use(pinia);
  app.use(store);
  app.use(VueApexCharts);
  app.use(router);

  fhirhelper.init(config.systemConfig);

  const confettiCleanup = initConfettiListeners(store);
  app.provide('confettiCleanup', confettiCleanup); // Make cleanup available to components if needed
  // Ensure confetti event listener is removed when the page unloads
  window.addEventListener('beforeunload', confettiCleanup);
  //Vue.use(VueTheMask);

  // setup a refresh timer for every 10 min, but trigger immediately
  setInterval(function refresh() {
    authhelper.refreshSession();
  }, 600000);

  // Register only essential components globally
  app.component("FailoverImage", failoverImage);
  app.component("Avatar", Avatar);
  app.component("DatePicker", DatePicker);
  app.component("InstanceSelector", InstanceSelector);
  // Other components should be registered locally in their parent components


  store.commit("organization/LOAD_ORGANIZATION_SUCCESS", config, { root: true });
  store.commit("instances/LOAD_INSTANCE_BY_ID_SUCCESS", config.instance, { root: true });
  store.commit("instances/LOAD_INSTANCES_SUCCESS", [config.instance], { root: true });
  store.commit("integrations/LOAD_INTEGRATIONS_SUCCESS", config.integrations, { root: true });
  if (config.forms) {
    store.commit("forms/FORMS_LOAD_SUCCESS", config.forms, { root: true });
  }
  store.commit("user/LOAD_ROLES_SUCCESS", config.roles, { root: true });
  store.commit("settings/LOAD_STATUSES_SUCCESS", config.statuses, { root: true });
  const notificationsStore = useNotificationsStore();
  notificationsStore.loadNotificationTypesSuccess(config.notificationTypes);
  app.provide('version', config.version);
  ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsCommunityModule)]);
  LicenseManager.setLicenseKey(config.agGridKey);
  app.provide('features', config.features);


  app.config.errorHandler = (err, vm, info) => {
    // Check if the error message matches the specific error
    if (err?.message?.includes('Failed to load module script: Expected a JavaScript module script but the server responded with a MIME type of "text/html"')) {
      // Refresh the page
      location.reload();
    } else {
      // Handle other errors
      if (config.environment !== 'localhost') {
        datadogLogs.logger.error(err);
      } else {
        console.error('Error in localhost environment:', err);
      }

      throw err; // Let the default error handler handle it
    }
  };

  let version = config.version ? config.version.replace(/[^a-z0-9_:./-]/g, '') : '';

  // COST OPTIMIZATION: Reduced telemetry sampling based on environment
  var telemetrySampleRate = config.environment === 'production' ? 20 :
                           config.environment === 'development' ? 5 : 10;

  var sharedConfig = {
    applicationId: 'e1f29b1f-ed19-4374-9d8f-408b58f62ac3',
    clientToken: 'pub26f3f50484f717d905addfcabb79fa1f',
    version: version,
    env: config.environment,
    service: 'web-app',
    site: 'us5.datadoghq.com',
    telemetrySampleRate: telemetrySampleRate
  };

  if (config.environment !== 'localhost') {
    datadogLogs.init({
      ...sharedConfig,
    })
    datadogRum.init({
      ...sharedConfig,
      // COST OPTIMIZATION: Reduced session sampling rate
      sessionSampleRate: config.environment === 'production' ? 25 : 10,
      trackResources: config.environment === 'production',
      trackLongTasks: config.environment === 'production',
      trackUserInteractions: config.environment === 'production',
      enablePrivacyForActionName: true,
      defaultPrivacyLevel: 'mask-user-input'
    });
  }
  app.mount("#app");
}
